import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CenterSelectionModal from '../components/booking/CenterSelectionModal';
import CalendarView from '../components/booking/CalendarView';
import BookingForm from '../components/booking/BookingForm';

const NewAppointmentBooking = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Initialize state based on location data
  const [step, setStep] = useState(() => {
    return location.state?.center && location.state?.slot ? 3 : 0;
  });
  const [selectedCenter, setSelectedCenter] = useState(() => {
    return location.state?.center || null;
  });
  const [selectedSlot, setSelectedSlot] = useState(() => {
    return location.state?.slot || null;
  });
  const [modalOpen, setModalOpen] = useState(false);

  const handleBookAppointmentClick = () => {
    setModalOpen(true);
    setStep(2);
  };

  const handleCenterSelect = (center) => {
    setSelectedCenter(center);
    setStep(2);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setStep(3);
  };

  const handleBackToCalendar = () => {
    setStep(2);
  };

  const handleBackToModal = () => {
    setSelectedSlot(null);
    setModalOpen(true);
    setStep(1);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-secondary to-white">
      <Header />

      <div className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div
                key="hero"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="text-center py-20"
              >
                <h2 className="text-xl md:text-4xl font-semibold text-primary mb-6">
                  Book Your Appointment
                </h2>
                <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                  Schedule your government service appointment in just a few simple steps.
                  Fast, secure, and convenient.
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleBookAppointmentClick}
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-accent hover:bg-accent/90 rounded-lg shadow-lg transition-all"
                  data-testid="book-appointment-hero-btn"
                >
                  <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Start Booking
                </motion.button>
              </motion.div>
            )}

            {step === 2 && selectedCenter && (
              <motion.div
                key="calendar"
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
              >
                <CalendarView
                  center={selectedCenter}
                  onSelectSlot={handleSlotSelect}
                  onBack={handleBackToModal}
                />
              </motion.div>
            )}

            {step === 3 && selectedCenter && selectedSlot && (
              <motion.div
                key="form"
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
              >
                <BookingForm
                  center={selectedCenter}
                  slot={selectedSlot}
                  onBack={handleBackToCalendar}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <CenterSelectionModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          if (step === 1) setStep(0);
        }}
        onSelectCenter={handleCenterSelect}
      />

      <Footer />
    </div>
  );
};

export default NewAppointmentBooking;